import {
  AIM_PROFILE_CHANGED,
  HUDDLE_PRESENCE,
  PRESENCE_CHANGED,
} from '@common/constants/webSockets'
import { IHuddlePresence } from '@common/interfaces/huddles'
import { IAimProfileState } from '@common/interfaces/user'
import { Action } from 'redux'
import {
  IListPresenceParams,
  LIST_PRESENCE_FAILURE,
  LIST_PRESENCE_REQUEST,
  LIST_PRESENCE_SUCCESS,
} from './actions'

/**
 * Who is online and what they're up to — for the whole app, not just messaging.
 *
 * Its own slice on purpose. Both of these started life inside the `channels`
 * slice, which is where AIM needed them, but presence and status are properties
 * of a *person*, not of a conversation: assignee lists, task rows and notes all
 * want them, and none of those should have to reach into the messaging slice to
 * ask whether someone is online.
 *
 * Fed by websocket pushes plus one seeding fetch. The pushes
 * (`PRESENCE_CHANGED`, `AIM_PROFILE_CHANGED`) are dispatched raw by
 * webSocketMiddleware as `{ type, data }`, so matching the constants is the whole
 * subscription; `listPresence` supplies the initial online set, which no push
 * can, because a push only fires on a transition.
 */
export interface IPresenceSliceState {
  /**
   * An array rather than a Set because redux state must stay serializable, and
   * the lists that read it are small enough that `includes` is not worth a Map.
   */
  onlineUserIds: string[]
  /**
   * Nickname/status overrides keyed by user id, holding only people who have
   * actually changed something this session.
   *
   * Deliberately sparse: the authoritative name still arrives pre-resolved on
   * each payload from the server, so this is an *overlay* for live edits, not the
   * source of truth for every user. An empty map is the normal state.
   *
   * Merged today by the AIM members panel. The task, note and assignee surfaces
   * do not read it yet — the plumbing is deliberately ahead of the UI there, so
   * adding presence or a status to one of them is a local change rather than a
   * new round trip.
   */
  profileByUserId: Record<string, IAimProfileState>
  /**
   * Transitions that arrived while a seed request was in flight, so the snapshot
   * cannot undo them.
   *
   * The seed is authoritative as of the instant the SERVER read Redis, not as of
   * when the reducer sees it. A PRESENCE_CHANGED landing in that window is newer
   * than the snapshot, so replacing wholesale would revert it — and presence only
   * pushes on a transition, so the dot would then stay wrong until that person
   * next connected or disconnected. Empty except during a seed.
   */
  transitionsDuringSeed: Record<string, boolean>
  /**
   * The seed whose response is allowed to land; null when none is in flight.
   *
   * Seeds overlap in practice — MainLayout fetches on mount and the socket
   * re-seeds on reconnect — and the window above is a single shared map, so
   * without this an OLDER seed resolving first would replay and clear the window,
   * and the newer seed's snapshot would then land on an empty window and revert
   * any transition that arrived in between. Because presence only pushes on a
   * transition, that wrong dot would persist until the person next connected or
   * disconnected. Each REQUEST takes over the id; a response carrying any other
   * id finished late and is dropped, the same guard `isStaleSearch` applies to
   * channel search. Dropping rather than merging is safe because the newer seed's
   * snapshot is at least as fresh as anything the older one could add.
   */
  seedId: number | null
  /** Who is in a huddle, for the headphones beside names. Seeded and replayed like `onlineUserIds`. */
  inHuddleUserIds: string[]
  huddleTransitionsDuringSeed: Record<string, boolean>
}

export const initialState: IPresenceSliceState = {
  onlineUserIds: [],
  profileByUserId: {},
  transitionsDuringSeed: {},
  seedId: null,
  inHuddleUserIds: [],
  huddleTransitionsDuringSeed: {},
}

/** A seed's set with the transitions that arrived during it replayed on top. */
const replay = (seeded: string[], transitions: Record<string, boolean>): string[] => {
  const ids = new Set(seeded)
  for (const [userId, isIn] of Object.entries(transitions)) {
    if (isIn) ids.add(userId)
    else ids.delete(userId)
  }
  return [...ids]
}

/** `ids` with `userId` moved to the end when it is in, or dropped when it is not. */
const toggleId = (ids: string[], userId: string, isIn: boolean): string[] => {
  const without = ids.filter((id) => id !== userId)
  return isIn ? [...without, userId] : without
}

type PresenceAction = Action & { data?: unknown; params?: IListPresenceParams }

/**
 * Whether a seed response belongs to a request that has since been replaced.
 * `undefined` never matches, so an action without a seed id can only be stale.
 */
const isStaleSeed = (action: PresenceAction, state: IPresenceSliceState): boolean =>
  action.params?.seedId !== state.seedId

const presenceReducer = (
  state: IPresenceSliceState = initialState,
  action: PresenceAction,
): IPresenceSliceState => {
  switch (action.type) {
    case PRESENCE_CHANGED: {
      const { userId, isOnline } = action.data as { userId: string; isOnline: boolean }
      return {
        ...state,
        onlineUserIds: toggleId(state.onlineUserIds, userId, isOnline),
        // Recorded unconditionally rather than only while a request is known to
        // be outstanding: the map is cleared when a seed resolves, so an entry
        // outside a seed window is harmless, and tracking "is a request in
        // flight" separately would be a second thing to keep correct.
        transitionsDuringSeed: { ...state.transitionsDuringSeed, [userId]: isOnline },
      }
    }

    case HUDDLE_PRESENCE: {
      const { userId, isInHuddle } = action.data as IHuddlePresence
      if (!userId) return state
      return {
        ...state,
        inHuddleUserIds: toggleId(state.inHuddleUserIds, userId, isInHuddle),
        huddleTransitionsDuringSeed: { ...state.huddleTransitionsDuringSeed, [userId]: isInHuddle },
      }
    }

    case LIST_PRESENCE_REQUEST:
      // Start a fresh window: only transitions from here on can be newer than
      // the snapshot this request is about to return. Taking over `seedId` also
      // retires any seed still in flight — its snapshot is older than this one.
      return {
        ...state,
        transitionsDuringSeed: {},
        huddleTransitionsDuringSeed: {},
        seedId: action.params?.seedId ?? null,
      }

    case LIST_PRESENCE_SUCCESS: {
      // A superseded seed finishing late. Its snapshot predates the request that
      // replaced it, and the window now belongs to that request, so there is
      // nothing here that is safe to apply.
      if (isStaleSeed(action, state)) return state

      // The snapshot replaces rather than merges — merging would keep anyone
      // whose "went offline" push was missed while the socket was down, which is
      // the whole reason for re-seeding. But any transition that arrived while
      // the request was in flight is NEWER than the snapshot, so it is replayed
      // on top instead of being reverted.
      const { onlineUserIds, inHuddleUserIds } = action.data as {
        onlineUserIds: string[]
        inHuddleUserIds?: string[]
      }
      return {
        ...state,
        onlineUserIds: replay(onlineUserIds ?? [], state.transitionsDuringSeed),
        transitionsDuringSeed: {},
        inHuddleUserIds: replay(inHuddleUserIds ?? [], state.huddleTransitionsDuringSeed),
        huddleTransitionsDuringSeed: {},
        seedId: null,
      }
    }

    case LIST_PRESENCE_FAILURE:
      // Guarded like SUCCESS: a superseded seed failing late must not close the
      // window the seed that replaced it is still relying on.
      if (isStaleSeed(action, state)) return state
      // Nothing to reconcile against; drop the window so it cannot leak into the
      // next seed.
      return { ...state, transitionsDuringSeed: {}, huddleTransitionsDuringSeed: {}, seedId: null }

    case AIM_PROFILE_CHANGED: {
      const profile = action.data as IAimProfileState
      if (!profile?.userId) return state
      return {
        ...state,
        profileByUserId: { ...state.profileByUserId, [profile.userId]: profile },
      }
    }

    default:
      return state
  }
}

export default presenceReducer
